import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
import toast from 'react-hot-toast';
import { FaPaperPlane, FaArrowLeft, FaUser, FaSpinner, FaCheck, FaCheckDouble } from 'react-icons/fa';

interface PrivateMessage {
  id: string;
  sender_id: string;
  receiver_id: string;
  content: string;
  is_read: boolean;
  created_at: string;
  sender_email?: string;
  sender_profile_image?: string;
  sender_full_name?: string;
}

interface Friend {
  id: string;
  email: string;
  profile_image: string | null;
  full_name: string | null;
  is_online: boolean;
}

export default function PrivateChat() {
  const [messages, setMessages] = useState<PrivateMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [friend, setFriend] = useState<Friend | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = getCurrentUser();

  const friendId = location.state?.friendId;

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    if (!friendId) {
      toast.error('No friend selected');
      navigate('/learners');
      return;
    }

    loadFriend();
    loadMessages();
    
    // Set up real-time subscription for new messages
    const subscription = supabase
      .channel(`private-chat-${currentUser.id}-${friendId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'private_messages',
        filter: `or(and(sender_id.eq.${currentUser.id},receiver_id.eq.${friendId}),and(sender_id.eq.${friendId},receiver_id.eq.${currentUser.id}))`,
      }, (payload) => {
        const newMsg = payload.new as PrivateMessage;
        setMessages(prev => [...prev, newMsg]);
        scrollToBottom();
        
        // Mark message as read if it's from the friend
        if (newMsg.sender_id === friendId) {
          markMessageAsRead(newMsg.id);
        }
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [currentUser, friendId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadFriend = async () => {
    try {
      const { data, error } = await supabase
        .from('stripeusers')
        .select('id, email, profile_image, full_name, is_online')
        .eq('id', friendId)
        .single();

      if (error) throw error;
      setFriend(data);
    } catch (error: any) {
      console.error('Error loading friend:', error);
      toast.error('Failed to load friend information');
      navigate('/learners');
    }
  };

  const loadMessages = async () => {
    try {
      const { data, error } = await supabase
        .from('private_messages')
        .select(`
          *,
          sender:stripeusers!private_messages_sender_id_fkey(email, profile_image, full_name)
        `)
        .or(`and(sender_id.eq.${currentUser?.id},receiver_id.eq.${friendId}),and(sender_id.eq.${friendId},receiver_id.eq.${currentUser?.id})`)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const formattedMessages = (data || []).map(msg => ({
        ...msg,
        sender_email: msg.sender?.email,
        sender_profile_image: msg.sender?.profile_image,
        sender_full_name: msg.sender?.full_name
      }));

      setMessages(formattedMessages);
      
      // Mark unread messages from friend as read
      const unreadMessages = formattedMessages.filter(msg => 
        msg.sender_id === friendId && !msg.is_read
      );
      
      if (unreadMessages.length > 0) {
        await markMultipleMessagesAsRead(unreadMessages.map(msg => msg.id));
      }
    } catch (error: any) {
      console.error('Error loading messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const markMessageAsRead = async (messageId: string) => {
    try {
      await supabase
        .from('private_messages')
        .update({ is_read: true })
        .eq('id', messageId)
        .eq('receiver_id', currentUser?.id);
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const markMultipleMessagesAsRead = async (messageIds: string[]) => {
    try {
      await supabase
        .from('private_messages')
        .update({ is_read: true })
        .in('id', messageIds)
        .eq('receiver_id', currentUser?.id);
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !currentUser || !friendId || sending) return;

    setSending(true);
    try {
      const { error } = await supabase
        .from('private_messages')
        .insert({
          sender_id: currentUser.id,
          receiver_id: friendId,
          content: newMessage.trim()
        });

      if (error) throw error;

      setNewMessage('');
    } catch (error: any) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading conversation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 p-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link
              to="/learners"
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <FaArrowLeft className="w-4 h-4 mr-2" />
              Back to Learners
            </Link>
            
            {friend && (
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-300">
                    {friend.profile_image ? (
                      <img
                        src={friend.profile_image}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-indigo-500 text-white font-bold">
                        {friend.email.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${
                    friend.is_online ? 'bg-green-500' : 'bg-gray-400'
                  }`}></div>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {friend.full_name || friend.email}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {friend.is_online ? 'Online' : 'Offline'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4" style={{ height: 'calc(100vh - 140px)' }}>
        <div className="max-w-4xl mx-auto space-y-4">
          {messages.length === 0 ? (
            <div className="text-center py-8">
              <FaUser className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Start a conversation</h3>
              <p className="text-gray-600">Send your first message to {friend?.full_name || friend?.email}</p>
            </div>
          ) : (
            messages.map((message) => (
              <div key={message.id} className={`flex ${message.sender_id === currentUser?.id ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[70%] ${message.sender_id === currentUser?.id ? 'order-2' : 'order-1'}`}>
                  <div className={`p-3 rounded-lg ${
                    message.sender_id === currentUser?.id
                      ? 'bg-indigo-600 text-white rounded-br-sm'
                      : 'bg-white border border-gray-200 text-gray-900 rounded-bl-sm'
                  }`}>
                    <p className="text-sm">{message.content}</p>
                  </div>
                  <div className={`mt-1 flex items-center space-x-1 ${
                    message.sender_id === currentUser?.id ? 'justify-end' : 'justify-start'
                  }`}>
                    <span className="text-xs text-gray-500">{formatTime(message.created_at)}</span>
                    {message.sender_id === currentUser?.id && (
                      <div className="text-xs">
                        {message.is_read ? (
                          <FaCheckDouble className="w-3 h-3 text-blue-500" title="Read" />
                        ) : (
                          <FaCheck className="w-3 h-3 text-gray-400" title="Sent" />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Message Input */}
      <div className="bg-white border-t border-gray-200 p-4">
        <div className="max-w-4xl mx-auto">
          <form onSubmit={sendMessage} className="flex items-center space-x-3">
            <div className="flex-1">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                disabled={sending}
              />
            </div>
            <button
              type="submit"
              disabled={sending || !newMessage.trim()}
              className="p-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {sending ? (
                <FaSpinner className="w-5 h-5 animate-spin" />
              ) : (
                <FaPaperPlane className="w-5 h-5" />
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}